import {
  MAX_UPLOAD_BYTES,
  platformProductUpdateRequestSchema,
  publishBlockerSchema,
  UPLOAD_IMAGE_TYPES,
  type ProductDetail,
  type ProductMedia,
  type ProductStatus,
  type ReplaceProductMediaRequest,
} from '@avrahami/contracts';
import {
  changedProductLists,
  fill,
  isApiError,
  productListsDraft,
  SVG_TYPE,
  textOrNull,
  type ProductListsDraft,
} from '@avrahami/ui';
import { he } from '../i18n/he';
import type { CopyField } from './copy-types';
import { parseWith } from './forms';

/**
 * One catalog product's page as the platform staff manage it (adr/0043): what its form holds and
 * how a save is sent. The page itself only presents.
 */

/** The statuses the page offers: a product is removed from a store by excluding it, not here. */
export const PAGE_STATUSES = ['DRAFT', 'ACTIVE', 'HIDDEN'] as const;
export type PageStatus = (typeof PAGE_STATUSES)[number];

export function isPageStatus(status: string): status is PageStatus {
  return (PAGE_STATUSES as readonly string[]).includes(status);
}

export interface SpecDraft {
  label: string;
  value: string;
}

export type ProductPageDraft = Record<CopyField, string> &
  ProductListsDraft & {
    status: PageStatus;
    extraSpecs: readonly SpecDraft[];
    /** The lists the draft was made from: only a list that differs is sent (adr/0055). */
    listsBase: ProductListsDraft;
  };

export function productPageDraft(product: ProductDetail): ProductPageDraft {
  const lists = productListsDraft(product.content);
  return {
    ...lists,
    listsBase: lists,
    title: product.content.title ?? '',
    description: product.content.description ?? '',
    seoTitle: product.content.seoTitle ?? '',
    seoDescription: product.content.seoDescription ?? '',
    // A listed product is never removed; a draft is the safe reading of anything else.
    status: isPageStatus(product.status) ? product.status : 'DRAFT',
    extraSpecs: product.facts.extraSpecs,
  };
}

/** Rows left completely empty are dropped; half-filled rows are kept (and rejected). */
function specRows(rows: readonly SpecDraft[]): SpecDraft[] {
  return rows
    .map((row) => ({ label: row.label.trim(), value: row.value.trim() }))
    .filter((row) => row.label !== '' || row.value !== '');
}

/**
 * The page as its save takes it (errors under `content.<field>`, `status` and
 * `facts.extraSpecs.<n>`), with the version the draft was made from; an emptied field is cleared.
 */
export function productPageRequest(draft: ProductPageDraft, version: number) {
  return parseWith(platformProductUpdateRequestSchema, {
    version,
    content: {
      title: textOrNull(draft.title),
      description: textOrNull(draft.description),
      seoTitle: textOrNull(draft.seoTitle),
      seoDescription: textOrNull(draft.seoDescription),
      // A list sent becomes the store's own (adr/0055): only the ones that changed.
      ...changedProductLists(draft, draft.listsBase),
    },
    status: draft.status,
    facts: { extraSpecs: specRows(draft.extraSpecs) },
  });
}

/** The first message under a path (`facts.extraSpecs` covers `facts.extraSpecs.2.label`). */
export function errorUnder(
  errors: Readonly<Record<string, string>>,
  prefix: string,
): string | undefined {
  const key = Object.keys(errors).find((path) => path === prefix || path.startsWith(`${prefix}.`));
  return key === undefined ? undefined : errors[key];
}

/** The catalog's facts of the product, as label and value rows (read-only on the page). */
export function factRows(product: ProductDetail): SpecDraft[] {
  const { facts } = product;
  const optional = (label: string, value: string | null) =>
    value === null ? [] : [{ label, value }];
  return [
    { label: he.productPage.factName, value: facts.name },
    { label: he.productPage.factSku, value: facts.sku },
    ...optional(he.productPage.barcode, facts.barcode),
    ...optional(he.productPage.model, facts.model),
    ...optional(he.productPage.manufacturer, facts.manufacturer),
    ...facts.specs,
  ];
}

export function statusLabel(status: ProductStatus): string {
  return he.productStatus[status];
}

/**
 * Why the product may not be shown in the store, when a save was refused for it (CONFLICT with
 * the blockers under `status`); null for any other outcome.
 */
export function publishRefusal(error: unknown): string | null {
  if (!isApiError(error, 'CONFLICT')) return null;
  const reasons = error.details.flatMap((detail) => {
    const blocker = publishBlockerSchema.safeParse(detail.message);
    return detail.path === 'status' && blocker.success ? [he.publishBlockers[blocker.data]] : [];
  });
  return reasons.length === 0
    ? null
    : fill(he.productPage.refused, { reasons: reasons.join(', ') });
}

// ---------------------------------------------------------------------------------------------
// The gallery
// ---------------------------------------------------------------------------------------------

/** One image of the gallery while it is being edited. */
export type GalleryItem =
  | { key: string; kind: 'EXISTING'; id: string; file: string; altText: string }
  | { key: string; kind: 'UPLOAD'; mediaId: string; file: string; altText: string };

export function galleryItems(media: readonly ProductMedia[]): GalleryItem[] {
  return [...media]
    .sort((first, second) => first.position - second.position)
    .map((image) => ({
      key: image.id,
      kind: 'EXISTING',
      id: image.id,
      file: image.file,
      altText: image.altText ?? '',
    }));
}

/** The gallery as the API replaces it: existing images by id, uploads by media id, in order. */
export function galleryRequest(
  items: readonly GalleryItem[],
  version: number,
): ReplaceProductMediaRequest {
  return {
    version,
    items: items.map((item) => {
      const altText = item.altText.trim() === '' ? null : item.altText.trim();
      return item.kind === 'EXISTING'
        ? { kind: 'EXISTING', id: item.id, altText }
        : { kind: 'UPLOAD', mediaId: item.mediaId, altText };
    }),
  };
}

/** The list with one item moved up (-1) or down (1); unchanged at an edge. */
export function moveItem<Item>(list: readonly Item[], index: number, delta: -1 | 1): Item[] {
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return [...list];
  const next = [...list];
  const [item] = next.splice(index, 1) as [Item];
  next.splice(target, 0, item);
  return next;
}

export type UploadProblem = 'TOO_LARGE' | 'NOT_AN_IMAGE';

/**
 * A quick check before uploading. The API decides the type from the bytes; this only saves a
 * round trip for files that are obviously wrong. An SVG passes: it is uploaded as a PNG.
 */
export function uploadProblem(file: { size: number; type: string }): UploadProblem | null {
  if (file.size > MAX_UPLOAD_BYTES) return 'TOO_LARGE';
  if (
    file.type !== '' &&
    file.type !== SVG_TYPE &&
    !(UPLOAD_IMAGE_TYPES as readonly string[]).includes(file.type)
  ) {
    return 'NOT_AN_IMAGE';
  }
  return null;
}
